"use client"

import { callbackPages, esIgnoreRedirectRouteList } from "@/config/consts"
import {
  PushNavigateOptions,
  useConfigParams,
  useMounted,
  useNavigation,
} from "@/hooks"
import { useFlow } from "@/hooks/use-flow"
import useSignupRequestStore from "@/store/signup-request-store"
import { usePathname } from "next/navigation"
import { ReactNode, useEffect } from "react"

import { useAnalyticsContext } from "@/components/analytics-provider"
import { Loader } from "@/components/ui/loader"
import { useCountriesQuery } from "@/services/get-countries"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

type RegisterParamsComponentProps = {
  children: ReactNode
}

/* -------------------------------------------------------------------------------------------------
 * component
 * -----------------------------------------------------------------------------------------------*/

function RegisterParams({ children }: RegisterParamsComponentProps) {
  /*--------------------hooks--------------------*/
  const mounted = useMounted()
  const { setData } = useSignupRequestStore()
  const {
    fromPdv,
    mppartner,
    camid,
    pid,
    affid,
    cliid,
    codicePromozione,
    intcmp,
    authType,
  } = useConfigParams()
  const pathName = usePathname()
  const router = useNavigation()
  const { initialFlowPath } = useFlow()

  const { sendData: setAnalyticsData } = useAnalyticsContext()
  useCountriesQuery()

  /*--------------------effects--------------------*/

  // useFeatureConfig()

  useEffect(() => {
    if (mppartner) {
      setData({ mppartner })
    }
  }, [mppartner, setData])

  useEffect(() => {
    if (camid) setData({ camid })
    if (pid) setData({ pid })
    if (affid) setData({ affid })
    if (cliid) setData({ cliid })
  }, [camid, pid, affid, cliid, setData])

  useEffect(() => {
    setData({ fromPdv })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromPdv])

  //save initial data into the store for later usage
  useEffect(() => {
    setData({
      initialHref: encodeURIComponent(window.location.href),
      authType,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //save promocode and intcmp to the store cuz we need to clean them up from the search params :(
  useEffect(() => {
    if (codicePromozione || intcmp) {
      setAnalyticsData({ welcome_bonus_name: codicePromozione })
      setData({
        promoCodeFromSearchParams: codicePromozione,
        intcmpFromSearchParams: intcmp,
      })
    }
  }, [codicePromozione, intcmp, setAnalyticsData, setData])

  //handle initial redirect
  useEffect(() => {
    const routeOptions: PushNavigateOptions = {
      cleanupParams: ["intcmp", "codicePromozione", "codicepromozione"],
    }

    const canRedirectAutomatically = !esIgnoreRedirectRouteList.some(
      (value) => pathName === value,
    )

    //atterriamo da uno dei flussi " esterni "
    const isFromCallbackRedirectPage = callbackPages.some((callbackPage) =>
      pathName.includes(callbackPage),
    )

    if (!canRedirectAutomatically || isFromCallbackRedirectPage) return

    router.replace(initialFlowPath, routeOptions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return <Loader />

  return children
}

export { RegisterParams }
export type { RegisterParamsComponentProps }
